import { reactive } from 'vue';
import { searchArena, acceptArena, cancelArena, type ArenaOpponent } from './arena-api';
import type { FightView } from './mobfight-api';
import { t } from '../i18n';

/**
 * THE QUEUE — Holmgang matchmaking as a module singleton, not screen state.
 *
 * It used to live inside Arena.vue, which meant leaving the Pit KILLED it:
 * the component's unmount hook cancelled the search, so a player who tabbed
 * over to check their stash lost their place in line (CU-86cb9yd3p follow-up:
 * "the search must not stop when you go to another page — like the little
 * corner panel in Dota"). A module survives navigation; the Pit and the
 * corner dock (QueueDock.vue) are now just two views of this one state.
 *
 * The server is still the authority on every instant that matters — `revealAt`
 * (when the fee starts applying) and `expiresAt` (when accept() stops taking
 * yes) both come from /arena/search. This store's interval only DERIVES the
 * phase from those timestamps against the wall clock, which also makes it
 * throttle-proof: a background tab's starved timers can delay a tick, but the
 * phase is recomputed from absolute times, so it is right the moment the
 * player looks back.
 */
export type QueuePhase = 'idle' | 'searching' | 'found';

interface PvpQueueState {
  phase: QueuePhase;
  /** Hidden until the reveal — the card and dock must not know the foe early. */
  opponent: ArenaOpponent | null;
  /** Wall-clock ms, ticked while the queue is live — read countdowns off this. */
  now: number;
  startedAt: number;
  revealAt: number;
  expiresAt: number;
  /** Rating a cancel costs once the foe is revealed — captured from the Pit's
   *  stakes when the search starts, so the dock can price its button even when
   *  the Pit is not mounted. */
  cancelRating: number;
  /** Receipt line ("walked away — 5 rating forfeited" / "the challenger
   *  walked") — a statement, not an error. */
  notice: string;
  error: string;
  busy: boolean;
}

export const pvpQueue = reactive<PvpQueueState>({
  phase: 'idle',
  opponent: null,
  now: Date.now(),
  startedAt: 0,
  revealAt: 0,
  expiresAt: 0,
  cancelRating: 0,
  notice: '',
  error: '',
  busy: false,
});

let ticker: number | null = null;
/** Invalidates in-flight search responses after a cancel/newer search. */
let epoch = 0;

function stopTicker() {
  if (ticker !== null) { window.clearInterval(ticker); ticker = null; }
}

function reset(phase: 'idle' = 'idle') {
  stopTicker();
  pvpQueue.phase = phase;
  pvpQueue.opponent = null;
  pvpQueue.startedAt = 0;
  pvpQueue.revealAt = 0;
  pvpQueue.expiresAt = 0;
}

/** Derive the phase from the server's own timestamps. */
function tick() {
  pvpQueue.now = Date.now();
  if (pvpQueue.phase === 'searching' && pvpQueue.revealAt > 0 && pvpQueue.now >= pvpQueue.revealAt) {
    pvpQueue.phase = 'found';
  }
  if (pvpQueue.phase === 'found' && pvpQueue.now >= pvpQueue.expiresAt) {
    // The hold lapsed server-side too (item 6) — no dead card, no dead dock.
    reset();
    pvpQueue.notice = t('arena.holdExpired');
  }
}

/**
 * Enter the queue. `cancelRating` is the Pit's `stakes.cancelRating`, carried
 * along so the dock can state the price without its own stats fetch. Throws
 * nothing: a failed search (no fury, live fight elsewhere) lands in `error`
 * and the queue returns to idle — same contract the Pit's axis always had.
 */
export async function startSearch(cancelRating: number): Promise<void> {
  if (pvpQueue.phase !== 'idle') return; // one queue; the UI disables re-entry
  const mine = ++epoch;
  pvpQueue.error = '';
  pvpQueue.notice = '';
  pvpQueue.cancelRating = cancelRating;
  pvpQueue.phase = 'searching';
  pvpQueue.startedAt = Date.now();
  pvpQueue.now = pvpQueue.startedAt;
  stopTicker();
  ticker = window.setInterval(tick, 1000);
  try {
    const opp = await searchArena();
    if (mine !== epoch) return; // cancelled (or replaced) while in flight
    // The server rolled the reveal; we just hold the foe back until then.
    pvpQueue.opponent = opp;
    pvpQueue.revealAt = opp.revealAt;
    pvpQueue.expiresAt = opp.expiresAt;
    tick(); // an already-past revealAt (clock skew) flips immediately
  } catch (e) {
    if (mine !== epoch) return;
    reset();
    pvpQueue.error = (e as Error).message;
  }
}

/**
 * Leave the queue / drop the found foe. Free while searching, the rating
 * penalty once revealed — the SERVER decides which (revealAt is its number);
 * `ratingLost` is what was actually taken, and the receipt quotes that, never
 * the constant. Returns the points lost (0 when nothing was charged).
 */
export async function cancelQueue(): Promise<number> {
  if (pvpQueue.phase === 'idle' || pvpQueue.busy) return 0;
  pvpQueue.busy = true;
  epoch += 1; // orphan any in-flight search response
  let ratingLost = 0;
  try {
    ({ ratingLost } = await cancelArena());
  } catch { /* idempotent — a failed cancel of nothing is still nothing */ }
  reset();
  if (ratingLost > 0) pvpQueue.notice = t('arena.cancelCharged', { rating: ratingLost });
  pvpQueue.busy = false;
  return ratingLost;
}

/**
 * Accept the found foe — spends the fury, starts the bout, empties the queue.
 * Returns the live FightView; the caller routes to the Pit, whose mount-time
 * resume picks the session up (it never starts a second one). Throws on a
 * refused accept (hold lapsed in the race) AFTER settling back to idle, so the
 * caller only has to show the message.
 */
export async function acceptQueue(): Promise<FightView> {
  if (pvpQueue.phase !== 'found' || pvpQueue.busy) throw new Error(t('arena.holdExpired'));
  pvpQueue.busy = true;
  try {
    const fight = await acceptArena();
    reset();
    return fight;
  } catch (e) {
    reset();
    pvpQueue.error = (e as Error).message;
    throw e;
  } finally {
    pvpQueue.busy = false;
  }
}

/** Clear the receipt/error lines (a new lobby visit starts clean). */
export function clearQueueNotices(): void {
  pvpQueue.error = '';
  pvpQueue.notice = '';
}
